import React, { useState, useEffect, useMemo } from 'react';
import { Card, Btn, Modal, Input, Select, Table, SectionHeader, NeedProject, StatCard, Badge, Tabs } from '../components/UI';
import { fmtDate, fmtNum, exportCSV } from '../utils';
import * as api from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import QSInstallationModal from '../components/QSInstallationModal';
import BulkQSInstallationModal from '../components/BulkQSInstallationModal';
import QSMatrixView from '../components/QSMatrixView';
import QSGroupedView from '../components/QSGroupedView';

const DEFAULT_COLS = [
  'sn',
  'installation_code',
  'installation_date',
  'project_code',
  'boq_code',
  'sub_boq',
  'material_name',
  'unit_of_measure',
  'actions'
];

const INITIAL_VISIBLE_COLS = [
  ...DEFAULT_COLS,
  'discipline',
  'boq_quantity',
  'issued_qty',
  'previous_installed_qty',
  'boq_desc'
];

const AVAILABLE_COLS = [
  { key: 'sn', label: 'S/N' },
  { key: 'installation_code', label: 'INSTALL ID' },
  { key: 'installation_date', label: 'DATE' },
  { key: 'project_code', label: 'PROJECT' },
  { key: 'boq_code', label: 'BOQ CODE' },
  { key: 'sub_boq', label: 'SUB BOQ CODE' },
  { key: 'material_name', label: 'MATERIAL' },
  { key: 'discipline', label: 'DISCIPLINE' },
  { key: 'unit_of_measure', label: 'UNIT' },
  { key: 'description', label: 'DESCRIPTION' },
  { key: 'boq_desc', label: 'BOQ DESC' },
  { key: 'section', label: 'SECTION' },
  { key: 'floor', label: 'FLOOR' },
  { key: 'zone', label: 'ZONE' },
  { key: 'room_area', label: 'ROOM/AREA' },
  { key: 'location_installed', label: 'LOCATION INSTALLED' },
  { key: 'sub_boq', label: 'SUB BOQ' },
  { key: 'boq_desc', label: 'BOQ DESC' },
  { key: 'cost_code', label: 'COST CODE' },
  { key: 'brand_name', label: 'BRAND' },
  { key: 'boq_quantity', label: 'BOQ QTY' },
  { key: 'purchased_qty', label: 'PURCHASED' },
  { key: 'issued_qty', label: 'ISSUED' },
  { key: 'previous_installed_qty', label: 'PREV INST' },
  { key: 'current_installed_qty', label: 'INST QTY' },
  { key: 'total_installed_qty', label: 'TOTAL INST' },
  { key: 'remaining_qty', label: 'REMAINING' },
  { key: 'installation_percentage', label: 'INST%' },
  { key: 'offcut_qty', label: 'OFFCUT' },
  { key: 'damaged', label: 'DAMAGED' },
  { key: 'returned_qty', label: 'RETURNED' },
  { key: 'wastage_qty', label: 'WASTAGE' },
  { key: 'lost_qty', label: 'LOST' },
  { key: 'reusable_offcut_qty', label: 'REUSABLE OFFCUT' },
  { key: 'uninstalled_issued_qty', label: 'UNINST ISSUED' },
  { key: 'variance_qty', label: 'VARIANCE' },
  { key: 'installer_name', label: 'INSTALLER' },
  { key: 'supervisor_name', label: 'SUPERVISOR' },
  { key: 'qs_employee_name', label: 'QS' },
  { key: 'qa_qc_status', label: 'QA/QC' },
  { key: 'inspection_status', label: 'INSPECTION' },
  { key: 'approval_status', label: 'APPROVAL' },
  { key: 'posted_status', label: 'POSTED' },
  { key: 'actions', label: 'ACTIONS' }
];

export default function QSSiteInstallationPage({ toast, projectId: propProjectId }) {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = propProjectId || params.projectId;

  const [loading, setLoading] = useState(true);
  const [subsections, setSubsections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [users, setUsers] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [successModal, setSuccessModal] = useState(false);

  // View State
  const [activeView, setActiveView] = useState('flat');
  const [expandedGroups, setExpandedGroups] = useState({});

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Column Settings
  const [showColSettings, setShowColSettings] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = localStorage.getItem('qsi_visible_cols');
    if (saved) {
      const parsed = JSON.parse(saved);
      const missingDefaults = DEFAULT_COLS.filter(c => !parsed.includes(c));
      return [...parsed, ...missingDefaults];
    }
    return INITIAL_VISIBLE_COLS;
  });

  const toggleCol = (key) => {
    if (DEFAULT_COLS.includes(key)) return;
    setVisibleCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem('qsi_visible_cols', JSON.stringify(next));
      return next;
    });
  };

  const loadData = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const [subsRes, txnRes, catRes, boqRes, discRes, userRes, costCatRes, summaryRes, matsRes] = await Promise.all([
        api.listDmrSubsections(projectId).catch(() => []),
        api.listDailyInstalled(projectId, 0, 500).catch(() => []),
        api.listDmrCategories().catch(() => []),
        api.listBOQ(projectId).catch(() => []),
        api.listDisciplines().catch(() => []),
        api.listUsers().catch(() => []),
        api.listCategories(false).catch(() => []),
        api.getDailyInstalledSummary(projectId).catch(() => null),
        api.listMaterials().catch(() => [])
      ]);
      setSubsections(Array.isArray(subsRes) ? subsRes : (subsRes?.data || subsRes?.items || []));
      setTransactions(Array.isArray(txnRes) ? txnRes : (txnRes?.data || txnRes?.items || []));
      setCategories(Array.isArray(catRes) ? catRes : (catRes?.data || catRes?.items || []));
      setBoqItems(Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []));
      setDisciplines(Array.isArray(discRes) ? discRes : (discRes?.data || discRes?.items || []));
      setUsers(Array.isArray(userRes) ? userRes : (userRes?.data || userRes?.items || []));
      setCostCategories(Array.isArray(costCatRes) ? costCatRes : (costCatRes?.data || costCatRes?.items || []));
      setMaterials(Array.isArray(matsRes) ? matsRes : (matsRes?.data || matsRes?.items || []));
      setSummaryStats(summaryRes);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [projectId]); // eslint-disable-line



  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await api.deleteDailyInstalled(deleteModal);
      toast.show("Installation entry deleted successfully", "success");
      loadData();
    } catch (e) {
      toast.show(e.message, "error");
    }
    setDeleteModal(null);
  };

  const getSubName = (id) => subsections.find(s => s.id === id)?.name || id;
  const getDisciplineName = (id) => disciplines.find(d => String(d.discipline_id || d.id) === String(id))?.name || id;
  const getBoqMainName = (id) => {
    if (!id) return '—';
    const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
    const item = itemsArray.find(b => String(b.boq_item_id) === String(id) || String(b.id) === String(id));
    return item ? (item.boq_code || item.item_code || item.code || id) : id;
  };
  const getBoqCompName = (boqId, compId) => {
    if (!compId) return '—';
    const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
    const item = itemsArray.find(b => String(b.boq_item_id) === String(boqId) || String(b.id) === String(boqId));
    if (item && item.components) {
      const compsArray = Array.isArray(item.components) ? item.components : (item.components?.data || item.components?.items || []);
      const comp = compsArray.find(c => String(c.component_id) === String(compId) || String(c.id) === String(compId));
      if (comp) return comp.component_code || comp.code || comp.description || comp.name || compId;
    }
    return compId;
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      if (filterStatus && t.status !== filterStatus) return false;
      if (filterSub && t.dmr_subsection_id !== filterSub) return false;
      if (filterDiscipline && t.discipline !== filterDiscipline) return false;
      if (search) {
        const q = search.toLowerCase();
        const code = (t.installation_code || '').toLowerCase();
        const brand = (t.brand_name || '').toLowerCase();
        const loc = (t.location || '').toLowerCase();
        if (!code.includes(q) && !brand.includes(q) && !loc.includes(q)) return false;
      }
      return true;
    });
  }, [transactions, search, filterStatus, filterSub, filterDiscipline]);

  const dashboardStats = useMemo(() => {
    let records = filteredTransactions.length;
    let installed = 0;
    let offcut = 0;
    let wastage = 0;
    let damaged = 0;

    const uniqueItems = new Map();

    filteredTransactions.forEach(t => {
      installed += Number(t.current_installed_qty || 0);
      offcut += Number(t.offcut_qty || 0);
      wastage += Number(t.wastage_qty || 0);
      damaged += Number(t.lost_qty || 0);

      const key = `${t.boq_item_code}_${t.boq_component_name}`;
      if (!uniqueItems.has(key)) {
        uniqueItems.set(key, {
          boq: Number(t.boq_quantity || 0),
          issued: Number(t.issued_qty || 0)
        });
      }
    });

    let boqQty = 0;
    let issued = 0;
    uniqueItems.forEach(val => {
      boqQty += val.boq;
      issued += val.issued;
    });

    let remaining = boqQty - installed;
    let overBoq = installed > boqQty ? installed - boqQty : 0;
    let instPercent = boqQty > 0 ? (installed / boqQty) * 100 : 0;

    return {
      records,
      boqQty,
      issued,
      installed,
      remaining: remaining > 0 ? remaining : 0,
      offcut,
      wastage,
      damaged,
      overBoq,
      instPercent
    };
  }, [filteredTransactions]);

  useEffect(() => {
    setPage(1);
  }, [search, filterStatus, filterSub, filterDiscipline]);

  const paginatedTransactions = useMemo(() => {
    if (pageSize === 'All') return filteredTransactions;
    const start = (page - 1) * pageSize;
    return filteredTransactions.slice(start, start + pageSize);
  }, [filteredTransactions, page, pageSize]);


  if (!projectId) return <NeedProject />;

  return (
    <div className="fade-in">
      <div style={{ 
        display: 'flex', gap: 12, overflowX: 'auto', padding: '12px', borderRadius: 12, border: '1px solid black', marginBottom: 24
      }}>
        {[
          { label: 'RECORDS', value: fmtNum(dashboardStats.records), color: '#1e3a8a' },
          { label: 'BOQ QTY', value: fmtNum(dashboardStats.boqQty), color: '#1e3a8a' },
          { label: 'ISSUED', value: fmtNum(dashboardStats.issued), color: '#2563eb' },
          { label: 'INSTALLED', value: fmtNum(dashboardStats.installed), color: '#16a34a' },
          { label: 'REMAINING', value: fmtNum(dashboardStats.remaining), color: '#b45309' },
          { label: 'OFFCUT', value: fmtNum(dashboardStats.offcut), color: '#2563eb' },
          { label: 'WASTAGE', value: fmtNum(dashboardStats.wastage), color: '#ea580c' },
          { label: 'DAMAGED', value: fmtNum(dashboardStats.damaged), color: '#dc2626' },
          { label: 'OVER BOQ', value: fmtNum(dashboardStats.overBoq), color: '#16a34a' },
          { label: 'INST%', value: `${dashboardStats.instPercent.toFixed(1)}%`, color: '#1e3a8a' },
        ].map((stat, idx) => (
          <div key={idx} style={{
            background: '#d4d7dbff', padding: '10px 16px', borderRadius: 8, 
            minWidth: 110, flex: 1, border: '1px solid black', flexShrink: 0
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{stat.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="flex-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>QS Site Installation Register</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => exportCSV(filteredTransactions, 'qs-installation-register')}>⬇ CSV</Btn>
          <Btn onClick={() => setCreateModal(true)}>Record Installation</Btn>
        </div>
      </div>

      {/* ROBUST FILTER BAR */}
      <Card style={{ padding: '20px 24px', marginBottom: 24, borderRadius: 12, border: '1px solid black' }}>
        <div className="flex-mobile-col" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16 }}>
          <div className="flex-mobile-col mobile-w-full" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Search Entries</div>
            <div className="mobile-w-full" style={{ flex: 1 }}>
              <Input placeholder="Search by code, brand, or location..." value={search} onChange={v => setSearch(v)} />
            </div>
          </div>
          <div className="flex-mobile-col mobile-w-full" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Btn variant="secondary" onClick={() => { setSearch(''); setFilterStatus(''); setFilterSub(''); setFilterDiscipline(''); }} style={{ whiteSpace: 'nowrap' }}>Clear Filters</Btn>
          </div>
        </div>

        <div className="grid-cols-3" style={{ display: 'grid', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Status</div>
            <Select value={filterStatus} onChange={setFilterStatus} options={[{ value: '', label: 'All Statuses' }, { value: 'DRAFT', label: 'DRAFT' }, { value: 'SUBMITTED', label: 'SUBMITTED' }, { value: 'APPROVED', label: 'APPROVED' }, { value: 'REJECTED', label: 'REJECTED' }]} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Sub-section</div>
            <Select value={filterSub} onChange={setFilterSub} options={[{ value: '', label: 'All Sub-sections' }, ...subsections.map(s => ({ value: s.id, label: s.name }))]} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Discipline</div>
            <Select value={filterDiscipline} onChange={setFilterDiscipline} options={[{ value: '', label: 'All Disciplines' }, ...disciplines.map(c => ({ value: c.name, label: c.name }))]} />
          </div>
        </div>
      </Card>

      <Tabs 
        tabs={[
          { id: 'flat', label: 'Flat View', icon: '☷' },
          { id: 'grouped', label: 'Grouped View', icon: '☰' },
          { id: 'matrix', label: 'QS Weekly Matrix', icon: '▦' }
        ]} 
        active={activeView} 
        onChange={setActiveView} 
      />

      {activeView === 'matrix' ? (
        <QSMatrixView transactions={filteredTransactions} />
      ) : activeView === 'grouped' ? (
        <QSGroupedView 
          transactions={filteredTransactions}
          getBoqMainName={getBoqMainName}
          getSubName={getSubName}
          getDisciplineName={getDisciplineName}
          setViewModal={setViewModal}
          setEditModal={setEditModal}
          setDeleteModal={setDeleteModal}
        />
      ) : (
      <Card style={{ border: '1px solid black', borderTop: '1px solid black', overflow: 'hidden', borderRadius: 8, borderTopLeftRadius: 0 }}>
        <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setShowColSettings(true)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#0b318bff', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 }} title="Choose Visible Columns">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            Column Settings
          </button>
        </div>
        <Table loading={loading} maxHeight="calc(100vh - 320px)" cols={[
          { align: 'center', key: 'sn', label: 'S/N', width: 50, stickyLeft: 0, render: (_, __, idx) => <span style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 600 }}>{(pageSize === 'All' ? 0 : (page - 1) * pageSize) + idx + 1}</span> },
          { align: 'center', key: 'installation_code', label: 'INSTALL ID', width: 140, stickyLeft: 50, render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--brand)', whiteSpace: 'nowrap', fontWeight: 600 }}>{v}</span> },
          { align: 'center', key: 'installation_date', label: 'DATE', width: 90, stickyLeft: 190, render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(v)}</span> },
          { align: 'center', key: 'project_code', label: 'PROJECT', width: 100, stickyLeft: 280, render: (v, row) => <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{row.project?.project_code || v || '—'}</span> },
          { align: 'center', key: 'boq_code', label: 'BOQ CODE', width: 100, stickyLeft: 380, render: (v, row) => <span style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.boq_item_code || getBoqMainName(row.boq_item_id)}</span> },
          { align: 'center', key: 'sub_boq', label: 'SUB BOQ CODE', width: 150, stickyLeft: 480, render: (v, row) => <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand)', whiteSpace: 'nowrap' }}>{row.boq_component_code || '—'}</span> },
          { align: 'center', key: 'material_name', label: 'MATERIAL', width: 150, stickyLeft: 630, render: v => <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', whiteSpace: 'nowrap' }}>{v || '—'}</span> },
          { align: 'center', key: 'unit_of_measure', label: 'UNIT', width: 70, stickyLeft: 780, isLastSticky: true, render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
          { align: 'center', key: 'discipline', label: 'DISCIPLINE', width: 100, render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.discipline || getDisciplineName(row.discipline_id) || '—'}</span> },

          { align: 'center', key: 'description', label: 'DESCRIPTION', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }} title={row.boq_component_name}>{row.boq_component_name || '—'}</span> },
          { align: 'center', key: 'boq_desc', label: 'BOQ DESC', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }} title={row.boq_item_description}>{row.boq_item_description || '—'}</span> },
          { align: 'center', key: 'section', label: 'SECTION', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.dmr_sub_section || getSubName(row.dmr_subsection_id) || '—'}</span> },
          { align: 'center', key: 'floor', label: 'FLOOR', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.zone || '—'}</span> },
          { align: 'center', key: 'zone', label: 'ZONE', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.zone || '—'}</span> },
          { align: 'center', key: 'room_area', label: 'ROOM/AREA', render: () => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'location_installed', label: 'LOCATION INSTALLED', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.location || '—'}</span> },
          { align: 'center', key: 'cost_code', label: 'COST CODE', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.cost_category || '—'}</span> },
          { align: 'center', key: 'brand_name', label: 'BRAND', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },

          { align: 'center', key: 'boq_quantity', label: 'BOQ QTY', render: v => <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'purchased_qty', label: 'PURCHASED', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'issued_qty', label: 'ISSUED', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'previous_installed_qty', label: 'PREV INST', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'current_installed_qty', label: 'INST QTY', render: v => <span style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'total_installed_qty', label: 'TOTAL INST', render: v => <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'remaining_qty', label: 'REMAINING', render: v => <span style={{ fontSize: 11, color: '#d97706', whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'installation_percentage', label: 'INST%', render: v => <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{v ? `${Number(v).toFixed(1)}%` : '—'}</span> },

          { align: 'center', key: 'offcut_qty', label: 'OFFCUT', render: v => <span style={{ fontSize: 11, color: 'var(--brand)', whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
          { align: 'center', key: 'damaged', label: 'DAMAGED', render: (v, row) => <span style={{ fontSize: 11, color: '#dc2626', whiteSpace: 'nowrap' }}>{fmtNum(0)}</span> },
          { align: 'center', key: 'returned_qty', label: 'RETURNED', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
          { align: 'center', key: 'wastage_qty', label: 'WASTAGE', render: v => <span style={{ fontSize: 11, color: '#d97706', whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
          { align: 'center', key: 'lost_qty', label: 'LOST', render: v => <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
          { align: 'center', key: 'reusable_offcut_qty', label: 'REUSABLE OFFCUT', render: v => <span style={{ fontSize: 11, color: '#16a34a', whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
          { align: 'center', key: 'uninstalled_issued_qty', label: 'UNINST ISSUED', render: v => <span style={{ fontSize: 11, color: '#16a34a', whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },
          { align: 'center', key: 'variance_qty', label: 'VARIANCE', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v || 0)}</span> },

          { align: 'center', key: 'installer_name', label: 'INSTALLER', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
          { align: 'center', key: 'supervisor_name', label: 'SUPERVISOR', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || (row.supervisor ? row.supervisor.first_name + ' ' + row.supervisor.last_name : '—')}</span> },
          { align: 'center', key: 'qs_employee_name', label: 'QS', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || (row.qs_employee ? row.qs_employee.first_name + ' ' + row.qs_employee.last_name : '—')}</span> },

          { align: 'center', key: 'qa_qc_status', label: 'QA/QC', render: v => v ? <Badge color={v.toLowerCase() === 'passed' ? 'green' : 'amber'}>{v}</Badge> : '—' },
          { align: 'center', key: 'inspection_status', label: 'INSPECTION', render: v => v ? <Badge color={v.toLowerCase() === 'passed' ? 'green' : 'amber'}>{v}</Badge> : '—' },
          {
            align: 'center', key: 'approval_status', label: 'APPROVAL', render: v => {
              let color = 'blue';
              if (v === 'Draft') color = 'gray';
              if (v === 'Approved') color = 'green';
              return v ? <Badge color={color}>{v}</Badge> : '—';
            }
          },
          { align: 'center', key: 'posted_status', label: 'POSTED', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v ? 'Yes' : 'No'}</span> },
          {
            key: 'actions', label: 'ACTIONS', render: (_, row) => (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                <Btn variant="secondary" size="sm" onClick={() => setViewModal(row)} title="View" style={{ padding: '6px 8px' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                </Btn>
                <>
                  <Btn variant="secondary" size="sm" onClick={() => setEditModal(row)} title="Edit" style={{ padding: '6px 8px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                  </Btn>
                  <Btn size="sm" style={{ background: 'var(--red)', color: 'white', borderColor: 'var(--red)', padding: '6px 8px' }} onClick={() => setDeleteModal(row.daily_installed_id)} title="Delete">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                  </Btn>
                </>
              </div>
            )
          },
        ].filter(c => visibleCols.includes(c.key))} rows={paginatedTransactions} emptyText="No installation entries." />

        {/* PAGINATION CONTROLS */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Show</span>
            <Select value={pageSize} onChange={v => { setPageSize(v === 'All' ? 'All' : Number(v)); setPage(1); }} options={[
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
              { value: 'All', label: 'All' }
            ]} style={{ minWidth: 80 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>entries</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {(() => {
                const totalCount = filteredTransactions.length;
                const label = 'entries';
                const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
                const end = pageSize === 'All' ? totalCount : Math.min(page * pageSize, totalCount);
                return `Showing ${start} to ${end} of ${totalCount} ${label}`;
              })()}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="secondary" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Btn>
              <Btn variant="secondary" disabled={pageSize === 'All' || page * pageSize >= filteredTransactions.length} onClick={() => setPage(p => p + 1)}>Next</Btn>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* BULK CREATION MODAL */}
      {createModal && (
        <BulkQSInstallationModal
          onClose={() => setCreateModal(false)}
          onSuccess={() => { setCreateModal(false); setSuccessModal('Entries created successfully'); loadData(); }}
          projectId={projectId}
          toast={toast}
          boqItems={boqItems}
          subsections={subsections}
          disciplines={disciplines}
          users={users}
          costCategories={costCategories}
          materials={materials}
        />
      )}

      {/* TRANSACTION MODAL (View/Edit) */}
      {(viewModal || editModal) && (
        <QSInstallationModal
          viewOnly={!!viewModal}
          transaction={viewModal || editModal}
          onClose={() => { setViewModal(null); setEditModal(null); }}
          onSuccess={(isEdit) => { setViewModal(null); setEditModal(null); setSuccessModal(isEdit ? 'Entry updated successfully' : 'Entry created successfully'); loadData(); }}
          projectId={projectId}
          toast={toast}
          boqItems={boqItems}
          subsections={subsections}
          categories={categories}
          disciplines={disciplines}
          users={users}
          costCategories={costCategories}
          materials={materials}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModal && (
        <Modal hideHeader width={380} onClose={() => setDeleteModal(null)}>
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', border: '1px solid #fca5a5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Delete Transaction</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: 'var(--text-secondary)' }}>
              Are you sure you want to delete this installation entry? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Btn variant="secondary" style={{ flex: 1, padding: '10px 0' }} onClick={() => setDeleteModal(null)}>Cancel</Btn>
              <Btn style={{ flex: 1, padding: '10px 0', background: '#ef4444', borderColor: '#ef4444', color: '#fff' }} onClick={confirmDelete}>Delete</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* SUCCESS CONFIRMATION MODAL */}
      {successModal && (
        <Modal hideHeader width={340} onClose={() => setSuccessModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '20px 10px 10px 10px' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: '#f0fdf4', border: '1px solid #bbf7d0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>Success</h3>
            <p style={{ margin: '0 0 32px 0', fontSize: 14, color: 'var(--text-secondary)' }}>{typeof successModal === 'string' ? successModal : 'Entry processed successfully'}</p>
            <Btn style={{ width: '100%', padding: '12px 0', background: '#16a34a', border: 'none', color: '#fff', fontSize: 15 }} onClick={() => setSuccessModal(false)}>Continue</Btn>
          </div>
        </Modal>
      )}

      {/* COLUMN SETTINGS MODAL */}
      {showColSettings && (
        <Modal title="Table Columns" onClose={() => setShowColSettings(false)} headerBg="#0b318b" headerColor="#ffffff" width={500}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
            {AVAILABLE_COLS.map(c => {
              const isDefault = DEFAULT_COLS.includes(c.key);
              const isChecked = visibleCols.includes(c.key);
              return (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isDefault ? 'not-allowed' : 'pointer', opacity: isDefault ? 0.7 : 1 }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDefault}
                    onChange={() => toggleCol(c.key)}
                    style={{ width: 16, height: 16, accentColor: '#0b318b' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{c.label}</span>
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: 24, padding: '12px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
            Settings are automatically saved to your browser.
          </div>
        </Modal>
      )}
    </div>
  );
}
